import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { getCookieConsentValue } from 'react-cookie-consent';
import { Dashboard } from './layouts/Dashboard';
import AuthLayout from './layouts/Auth.js';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import Amplify, { Auth } from 'aws-amplify';
import { AWS_CONFIG, environmentName } from './config';
import { connect, useDispatch, useSelector } from 'react-redux';
import { setRefetch, setValue } from './redux/actions';
import TermsAndConditionsPage from './pages/Auth/TermsAndConditionsPage';
import { reset, track, register } from 'tracking/mixpanel';
import _ from 'lodash';
import { CHAT_V2_SET_RECIPIENTS_LIST } from 'redux/types';
import { vendorContactRoleHumanReadable } from 'utils';
import {
  SET_ACCOUNT,
  SET_LENDER_PROFILE,
  SET_RELATED_USERS,
  SET_USER_PROFILE,
  SET_PORTAL_NOTIFICATIONS,
} from 'redux/types';
import { CommonDataContext } from './contexts/CommonDataContext';
import RedFlagsiFrame from 'pages/RedFlagsiFrame.js';
import UserSessionControl from 'components/UserSessionControl';
import { CircularProgress, Backdrop } from '@mui/material';
import logo from 'assets/img/trnsact-logo-dark.png';
import Theme from './theme';
import { ViewTypeContextProvider } from './contexts/contentViewType';
import { CommonModals } from './components/shared/Modals';
import { QUERY_PORTAL_CONFIGURATION } from './queries/common';
import { portalConfigurationsActions } from './redux/portalConfigurationReducer';
import {
  initialPortalConfigTypes,
  PORTAL_NOTIFICATIONS_STATUS_POLL_INTERVAL,
  CONSENT_COOKIE_NAME,
} from './constants/common';
import { getDefaultPortalConfigurationByType } from './services/pcwService';
import { NotificationProvider } from './modules/notification';
import { Constructor } from 'pages/DynamicOCA/Constructor';
import { Userpilot } from 'userpilot';
import { portalConfigurationTypes } from './pages/Prequal/constants';
import { AftermarketLicenceType } from 'modules/desking/types';
import { usePortalConfigurationMergeSavedWithDefault } from './modules/portalConfigurations/hooks';
import { broadcastApi } from './modules/broadcasts';
import { appApi } from './appApi';
import moment from 'moment';
import { logError } from './utils/logger';
import { publicRoutes } from './constants/routes';
import { AdminRole } from '@trnsact/trnsact-shared-types';

Auth.configure({ AWS_CONFIG });
Amplify.configure(AWS_CONFIG);

const LOGGED_IN_FLAG_NAME = 'LoggedIn';

function App({ setValue, reduxRefetch, setRefetch, portalConfiguration }) {
  //We decided to revert the cookie consent banner for now, but it will be re-enabled in future, so keep all the logic
  const isCookieConsent = true; //  getCookieConsentValue(CONSENT_COOKIE_NAME) === 'true';

  const dispatch = useDispatch();
  const chatOpened = useSelector(state => state.chatV2.open);
  const [currentAuthenticatedUser, setCurrentAuthenticatedUser] = useState(undefined);
  const [showPrequal, setShowPrequal] = useState(false);

  const [isDealerUser, setIsDealerUser] = useState(false);
  const [isLenderUser, setIsLenderUser] = useState(false);
  const [isCommonPartner, setIsCommonPartner] = useState(false);
  const [isAftermarketPartner, setIsAftermarketPartner] = useState(false);

  const [siteLoading, setSiteLoading] = useState(true);

  const { mergeSavedConfigWithDefault } = usePortalConfigurationMergeSavedWithDefault();

  const [getRelatedUsers, { data: dealerRelatedUsersData, refetch: relatedUsersRefetch }] = useLazyQuery(
    appApi.getDealerRelatedUsers,
    {
      context: { authRequired: true },
    }
  );

  const [getLenderRelatedUsers, { data: lenderRelatedUsersData }] = useLazyQuery(appApi.getLenderRelatedUsers, {
    context: { authRequired: true },
  });

  const [getAccountData, { data: accountData, refetch: accountDataRefetch, loading: loadingAccountData }] =
    useLazyQuery(appApi.getAccount, {
      fetchPolicy: 'cache-and-network',
      context: { authRequired: true },
      onCompleted: () => {
        setSiteLoading(false);
      },
    });

  useQuery(QUERY_PORTAL_CONFIGURATION, {
    context: { authRequired: true },
    fetchPolicy: 'no-cache',
    skip: !accountData?.vendorProfile?.id,
    variables: {
      vendorProfileId: accountData?.vendorProfile?.id,
      types: initialPortalConfigTypes,
    },
    onCompleted(data) {
      const portalConfigurationsWithDefault = initialPortalConfigTypes.map(configType => {
        const savedConfig = data?.portalConfigurations?.find(({ type }) => type === configType);
        return savedConfig
          ? mergeSavedConfigWithDefault(savedConfig, accountData?.vendorProfile?.id)
          : getDefaultPortalConfigurationByType(configType, accountData?.vendorProfile?.id);
      });

      dispatch(portalConfigurationsActions.setConfigData(portalConfigurationsWithDefault));
    },
  });

  useQuery(broadcastApi.getPortalNotificationsStatus, {
    fetchPolicy: 'no-cache',
    pollInterval: PORTAL_NOTIFICATIONS_STATUS_POLL_INTERVAL,
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      const portalNotificationsStatus = data?.broadcast?.portalNotificationsStatus;
      if (portalNotificationsStatus) {
        dispatch({ type: SET_PORTAL_NOTIFICATIONS, payload: portalNotificationsStatus });
      }
    },
  });

  const [getLenderProfile, { data: lenderProfile }] = useLazyQuery(appApi.getLenderProfile, {
    context: { authRequired: true },
  });

  const [onUpdateUserProfile] = useMutation(appApi.updateUserProfile);
  const [onFetchUserLoginMeta] = useLazyQuery(appApi.getUserLoginMeta);

  const onSetLoginDate = async () => {
    try {
      const { data } = await onFetchUserLoginMeta();

      if (!data?.userProfile?.dynamicsContactId) {
        logError('[onSetLoginDate]', 'userProfile -> dynamicsContactId is undefined');
        return;
      }

      const inNeedUpdateFirstLoginAndStatus = data.userProfile.status === 'approved';

      await onUpdateUserProfile({
        variables: {
          entityId: data.userProfile.dynamicsContactId,
          fieldsObj: {
            lastLogin: moment().toISOString(),
            ...(inNeedUpdateFirstLoginAndStatus && {
              status: 'active',
              firstLogin: moment().toISOString(),
            }),
          },
        },
      });
    } catch (error) {
      logError('Login data not saved', error);
    }
  };

  useEffect(() => {
    if (loadingAccountData) {
      console.log('%cLoading Account Data', 'color:green');
      setSiteLoading(true);
    }
  }, [loadingAccountData]);

  useEffect(() => {
    // async loading of the current user
    Auth.currentAuthenticatedUser()
      .then(user => {
        setCurrentAuthenticatedUser(user);

        //UX improvement: setting the backgrop while we load the account data
        setSiteLoading(true);
        getAccountData();
      })
      .catch(() => {
        setCurrentAuthenticatedUser(null);
        reset();
      });
  }, []);

  useEffect(() => {
    if (_.isEmpty(accountData)) {
      return;
    }
    const userPilotTrackingData = () => {
      if (!accountData.account || !accountData.userProfile) {
        return;
      }

      const { account, userProfile } = accountData;
      const aftermarketLicenceType = _.get(
        portalConfiguration,
        `${portalConfigurationTypes.aftermarket}.jsonDefinition.config.aftermarketLicenceType`,
        AftermarketLicenceType.Core
      );

      const deskingTabHiddenFor = _.get(
        portalConfiguration,
        `${portalConfigurationTypes.voManagement}.jsonDefinition.config.deskingTabHiddenFor`,
        []
      );
      const deskingAccess = !deskingTabHiddenFor.includes(userProfile.vendorContactRole);

      Userpilot.identify(userProfile.id, {
        name: userProfile.fullName,
        email: userProfile.email,
        accountType: userProfile.portalType === '1' ? 'Dealer' : 'Lender Partner',
        role: userProfile.vendorContactRole || userProfile.lenderContactRole,
        userAdminRole: userProfile.adminRole,
        environmentName,
        deskingAccess,
        company: {
          id: account.id,
          name: account.name,
          aftermarketLicenceType,
        },
      });
    };
    if (isCookieConsent) {
      userPilotTrackingData();
    }
    // did we just log in?
    if (sessionStorage.getItem(LOGGED_IN_FLAG_NAME)) {
      // clearing the flag that we just logged in
      sessionStorage.removeItem(LOGGED_IN_FLAG_NAME);
      // tracking the "Login" event
      track('Login');
    }

    const isPartner = accountData.account.accountType === 'Partner';
    const isAftermarketPartner = isPartner && accountData.partner.isAftermarketProd;
    setIsDealerUser(accountData.userProfile.portalType === '1');
    setIsLenderUser(accountData.userProfile.portalType === '2');
    setIsCommonPartner(isPartner);
    setIsAftermarketPartner(isAftermarketPartner);

    setValue('account', { ...accountData.account, partner: _.get(accountData, 'partner') });
    setValue('accountRep', accountData.accountRep);
    setValue('userProfile', accountData.userProfile);
    setValue('vp', accountData.vendorProfile);

    const lenderProfiles = _.get(accountData, 'vendorProfile.lenderProfiles', null);
    setValue('lp', lenderProfiles?.filter(lp => !lp.isDisabled) || []);
    setValue('disabledLenderProfiles', lenderProfiles?.filter(lp => lp.isDisabled) || []);
  }, [accountData, portalConfiguration]);

  useEffect(() => {
    if (!isLenderUser) {
      return;
    }
    if (_.isEmpty(accountData)) {
      return;
    }
    getLenderProfile();

    dispatch({ type: SET_ACCOUNT, payload: { ...accountData.account, partner: _.get(accountData, 'partner') } });
    dispatch({ type: SET_USER_PROFILE, payload: accountData.userProfile });

    getLenderRelatedUsers({
      variables: { dynamicsId: accountData.userProfile.dynamicsContactId },
    });
    register({
      accountId: _.get(accountData, 'account.id'),
      accountName: _.get(accountData, 'account.name'),
      dynamicsAccountId: _.get(accountData, 'account.dynamicsAccountId'),
      environment: environmentName,
      portal: 'lender',
    });
  }, [isLenderUser, accountData]);

  useEffect(() => {
    if (!isDealerUser) {
      return;
    }
    if (_.isEmpty(accountData)) {
      return;
    }
    console.log('Account Data', accountData);

    const mouseflowTrackingData = accountData => {
      window.waitForGlobal('_mfq', function () {
        //Mouseflow Account Name
        if (_.get(accountData, 'account.name', false)) {
          window._mfq.push(['setVariable', 'account', _.get(accountData, 'account.name')]);
        }

        //Mouseflow VP Settings
        if (_.get(accountData, 'vendorProfile.id', false)) {
          window._mfq.push(['setVariable', 'vendorProfileId', _.get(accountData, 'vendorProfile.id')]);
          window._mfq.push(['setVariable', 'vpSettingShowPrequal', _.get(accountData, 'vendorProfile.showPrequal')]);
          window._mfq.push([
            'setVariable',
            'vpSettingShowPortalCreditApp',
            _.get(accountData, 'vendorProfile.showPortalCreditApp'),
          ]);
          window._mfq.push(['setVariable', 'vpSettingShowDocMgt', _.get(accountData, 'vendorProfile.showDocMgt')]);
          window._mfq.push([
            'setVariable',
            'vpSettingShowLenderDocsAccessMgmt',
            _.get(accountData, 'vendorProfile.showLenderDocsAccessMgmt'),
          ]);
          window._mfq.push([
            'setVariable',
            'vpSettingShowCreditApp',
            _.get(accountData, 'vendorProfile.showCreditApp'),
          ]);
          window._mfq.push([
            'setVariable',
            'vpSettingShowPsOptions',
            _.get(accountData, 'vendorProfile.showPsOptions'),
          ]);
          window._mfq.push([
            'setVariable',
            'vpSettingShowMarginTool',
            _.get(accountData, 'vendorProfile.showMarginTool'),
          ]);
          window._mfq.push(['setVariable', 'vpSettingShowChat', _.get(accountData, 'vendorProfile.showChat')]);
          window._mfq.push(['setVariable', 'vpSettingShowTasks', _.get(accountData, 'vendorProfile.showTasks')]);
          window._mfq.push([
            'setVariable',
            'vpSettingShowRouteOneApp',
            _.get(accountData, 'vendorProfile.showRouteOneApp'),
          ]);
          window._mfq.push(['setVariable', 'vpSettingShowProposal', _.get(accountData, 'vendorProfile.showProposal')]);
          window._mfq.push([
            'setVariable',
            'vpSettingShowAftermarketOptions',
            _.get(accountData, 'vendorProfile.showAftermarketOptions'),
          ]);
          window._mfq.push(['setVariable', 'vpSettingShowPqt', _.get(accountData, 'vendorProfile.showPqt')]);
          window._mfq.push([
            'setVariable',
            'vpSettingCreditSubmissionDisplay',
            _.get(accountData, 'vendorProfile.creditSubmissionDisplay'),
          ]);
          window._mfq.push([
            'setVariable',
            'vpSettingShowLenderSubmission',
            _.get(accountData, 'vendorProfile.showLenderSubmission'),
          ]);
        }
        //Mouseflow User Profile Data
        if (_.get(accountData, 'userProfile.fullName', false)) {
          window._mfq.push(['setVariable', 'userFullname', _.get(accountData, 'userProfile.fullName')]);
          window._mfq.push(['setVariable', 'userEmail', _.get(accountData, 'userProfile.email')]);
          window._mfq.push(['setVariable', 'userPortalType', _.get(accountData, 'userProfile.portalType')]);
          window._mfq.push(['setVariable', 'userDynamicsId', _.get(accountData, 'userProfile.dynamicsContactId')]);
          window._mfq.push(['setVariable', 'userVendorRole', _.get(accountData, 'userProfile.vendorContactRole')]);
          window._mfq.push(['setVariable', 'userMobilePhone', _.get(accountData, 'userProfile.mobilePhoneNumber', '')]);
        }
      });
    };

    getRelatedUsers({
      variables: { accountId: _.get(accountData, 'account.id', '') },
    });
    setShowPrequal(!!accountData.vendorProfile?.showPrequal);
    if (isCookieConsent) {
      mouseflowTrackingData(accountData);
    }

    register({
      accountId: _.get(accountData, 'account.id'),
      accountName: _.get(accountData, 'account.name'),
      dynamicsAccountId: _.get(accountData, 'account.dynamicsAccountId'),
      environment: environmentName,
      portal: 'dealer',
    });
  }, [isDealerUser, accountData]);

  useEffect(() => {
    if (_.isEmpty(lenderProfile)) {
      return;
    }

    const mouseflowTrackingData = () => {
      try {
        window.waitForGlobal('_mfq', function () {
          //Mouseflow Account Name
          if (_.get(accountData, 'account.name', false)) {
            window._mfq.push(['setVariable', 'account', _.get(accountData, 'account.name')]);
          }

          //Mouseflow User Profile Data
          if (_.get(accountData, 'userProfile.fullName', false)) {
            window._mfq.push(['setVariable', 'userFullname', _.get(accountData, 'userProfile.fullName')]);
            window._mfq.push(['setVariable', 'userEmail', _.get(accountData, 'userProfile.email')]);
            window._mfq.push(['setVariable', 'userPortalType', _.get(accountData, 'userProfile.portalType')]);
            window._mfq.push(['setVariable', 'userDynamicsId', _.get(accountData, 'userProfile.dynamicsContactId')]);
            window._mfq.push([
              'setVariable',
              'userMobilePhone',
              _.get(accountData, 'userProfile.mobilePhoneNumber', ''),
            ]);
          }
          if (_.get(lenderProfile, 'lenderProfile.id', false)) {
            window._mfq.push(['setVariable', 'lenderProfileDynamicsId', _.get(lenderProfile, 'lenderProfile.id')]);
          }
        });
      } catch (error) {
        console.error(error);
      }
    };

    dispatch({ type: SET_LENDER_PROFILE, payload: lenderProfile.lenderProfile });
    if (isCookieConsent) {
      mouseflowTrackingData();
    }
  }, [lenderProfile, accountData]);

  // Dealer related users
  useEffect(() => {
    const { relatedUsers } = dealerRelatedUsersData || {};
    if (_.isEmpty(relatedUsers) || !isDealerUser) {
      return;
    }
    setValue('relatedUsers', relatedUsers);
    let formattedRecipients = [];

    //Related Dealers
    formattedRecipients = relatedUsers.map(user => {
      const mobilePhoneNumber = user.mobilePhoneNumber ? user.mobilePhoneNumber.replace(/\D+/g, '') : null;
      const fullName = `${user.firstName} ${user.lastName}${
        user.title ? ` <${user.title}>` : ` <${vendorContactRoleHumanReadable[user.vendorContactRole]}>`
      }`;

      return {
        fullName,
        identity: fullName,
        id: user.id,
        mobilePhoneNumber,
        mugshot: user.mugshot,
        email: user.email,
        isDealer: true,
        isLender: false,
        isApplicant: false,
        category: 'Internal',
      };
    });

    //Related Lenders
    if (_.get(accountData, 'vendorProfile.lenderProfiles', false)) {
      const lpsFiltered = accountData.vendorProfile.lenderProfiles
        .map(lp => {
          const user = lp.lenderUserProfile;
          if (!user) return;
          const mobilePhoneNumber = user.mobilePhoneNumber ? user.mobilePhoneNumber.trim().replace(/\D+/g, '') : null;
          const fullName = `${user.firstName} ${user.lastName}${user.title ? ` <${user.title}>` : ` <Lender>`}`;

          return {
            fullName,
            identity: fullName,
            id: user.id,
            mobilePhoneNumber,
            email: user.email,
            mugshot: user.mugshot,
            isDealer: false,
            isLender: true,
            isApplicant: false,
            category: 'Lenders',
          };
        })
        .filter(x => {
          if (x !== null) {
            return x;
          }
        });
      formattedRecipients.push(...lpsFiltered);
    }

    dispatch({
      type: CHAT_V2_SET_RECIPIENTS_LIST,
      payload: formattedRecipients,
    });
  }, [dealerRelatedUsersData]);

  // Lender related users
  useEffect(() => {
    const { lendersRelatedUsers } = lenderRelatedUsersData || {};
    if (_.isEmpty(lendersRelatedUsers) || !isLenderUser) {
      return;
    }

    const formattedRecipients = lendersRelatedUsers.map(user => {
      const mobilePhoneNumber = user.mobilePhoneNumber ? user.mobilePhoneNumber.replace(/\D+/g, '') : null;
      const fullName = `${user.firstName} ${user.lastName}${
        user.title ? ` <${user.title}>` : ` <${vendorContactRoleHumanReadable[user.vendorContactRole]}>`
      }`;
      const company = user.account.name;
      return {
        fullName,
        identity: fullName,
        id: user.id,
        mobilePhoneNumber,
        mugshot: user.mugshot,
        email: user.email,
        isDealer: true,
        isLender: false,
        isApplicant: false,
        category: 'Dealers',
        company,
      };
    });

    dispatch({
      type: CHAT_V2_SET_RECIPIENTS_LIST,
      payload: formattedRecipients,
    });

    dispatch({
      type: SET_RELATED_USERS,
      payload: lendersRelatedUsers,
    });
  }, [lenderRelatedUsersData]);

  useEffect(() => {
    if (chatOpened) {
      window.waitForGlobal('_mfq', function () {
        window._mfq.push(['tag', 'chat-open']);
        window._mfq.push(['newPageView', `/chat-widget`]);
      });
    }
  }, [chatOpened]);

  const onLoginSuccess = async user => {
    setCurrentAuthenticatedUser(user);
    await onSetLoginDate();

    // setting super property "email"
    register({ email: _.get(user, 'attributes.email') });
    // remembering that we just logged in. We do not send an event here to grab accountID first.
    sessionStorage.setItem(LOGGED_IN_FLAG_NAME, true);

    try {
      const queryParams = new URLSearchParams(window.location.search);
      const redirectTo = queryParams.get('redirectTo');
      if (redirectTo) window.location.href = redirectTo;
      else window.location.href = '/';
    } catch (e) {
      console.log(e);
      window.location.href = '/';
    }
  };

  if (reduxRefetch.account) {
    setRefetch('account', false);
    accountDataRefetch();
  }

  if (reduxRefetch.relatedUsers) {
    setRefetch('relatedUsers', false);
    relatedUsersRefetch();
  }

  //Forced to render as public page
  if (window.location.pathname.indexOf('/terms-and-conditions') !== -1) {
    return <TermsAndConditionsPage />;
  }

  const publicRoute = _.find(publicRoutes, pr => window.location.href.includes(pr.basePath));
  if (!!publicRoute && !currentAuthenticatedUser) {
    return (
      <Router>
        <Switch>
          <Route path={publicRoute.path} component={publicRoute.component} />
        </Switch>
      </Router>
    );
  }

  return (
    <Router>
      <CommonDataContext.Provider
        value={{
          isDealerUser,
          isLenderUser,
          isCommonPartner,
          isAftermarketPartner,
          isSuperAdmin: accountData?.userProfile?.adminRole === AdminRole.Super,
          isSingleAccountOnly: accountData?.userProfile?.adminRole === AdminRole.SingleAccountOnly,
          isSuperAdminOrSingleAccount: [AdminRole.Super, AdminRole.SingleAccountOnly].includes(
            accountData?.userProfile?.adminRole
          ),
        }}
      >
        <ViewTypeContextProvider>
          <Theme>
            <CommonModals />

            {window.location.href.includes('/red-flags-report') ? (
              <RedFlagsiFrame />
            ) : (
              <>
                {siteLoading && (
                  <Backdrop sx={{ color: '#fff', zIndex: -1 }} open={siteLoading}>
                    <CircularProgress sx={{ margin: '32px', color: '#9e9e9e' }} size={48} />
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img src={logo} style={{ maxWidth: '200px' }} />
                    </div>
                  </Backdrop>
                )}
                {accountData && (
                  <UserSessionControl
                    userRole={
                      _.get(accountData, 'userProfile.portalType') === '1'
                        ? _.get(accountData, 'userProfile.vendorContactRole') || 'dealer'
                        : _.get(accountData, 'userProfile.lenderContactRole') || 'lender'
                    }
                  />
                )}
                {currentAuthenticatedUser === undefined && <div />}
                {currentAuthenticatedUser !== null && currentAuthenticatedUser !== undefined && accountData && (
                  <Switch>
                    {window.location.pathname === '/constructor' ? (
                      <Constructor accountData={accountData} />
                    ) : (
                      <Dashboard />
                    )}
                  </Switch>
                )}

                {currentAuthenticatedUser === null && (
                  <Switch>
                    <AuthLayout
                      currentAuthenticatedUser={currentAuthenticatedUser}
                      onLoginSuccess={onLoginSuccess}
                      onLoginError={console.log}
                      onPasswordResetSuccess={() => {
                        console.log('Successfully submitted new password');
                        window.location.href = '/';
                      }}
                      onPasswordResetError={console.log}
                    />
                  </Switch>
                )}
              </>
            )}
          </Theme>
        </ViewTypeContextProvider>

        <NotificationProvider />
      </CommonDataContext.Provider>
    </Router>
  );
}

const mapStateToProps = state => {
  return {
    userProfile: state.userProfile,
    reduxRefetch: state.refetch,
    portalConfiguration: state.portalConfiguration,
  };
};

const mapDispatchToProps = {
  setValue,
  setRefetch,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
